import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { EmptyState, LoadingState } from '../../components/ui/Feedback';
import { Modal } from '../../components/ui/Modal';
import { PatientUhidAutocomplete } from '../../components/ui/PatientUhidAutocomplete';
import { FieldError } from '../../components/ui/FieldError';
import { ApiError } from '../../lib/api-client';
import { usePatientLookupSearch } from '../../lib/patient-uhid-search';
import { useToast } from '../../lib/toast';
import { useCreateOpdAppointment, useOpdLookup, useOpdSchedules } from './opd.queries';
import { FieldLabel, LookupSelect, PatientDemographicFields } from './opd.shared';
import { blankPatientFields, formatOpdScheduleDate, formatOpdScheduleTime, patientFieldsFromOption, todayInput } from './opd.utils';
import type { OpdLookupOption, PatientFormFields } from './types';

export const OpdAppointmentFormPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [params] = useSearchParams();
  const mutation = useCreateOpdAppointment();
  const departments = useOpdLookup('departments');
  const [department, setDepartment] = useState('');
  const doctors = useOpdLookup('doctors', '', department);
  const [doctor, setDoctor] = useState(params.get('doctor') ?? '');
  const schedules = useOpdSchedules(doctor, todayInput(), '', true);
  const [slot, setSlot] = useState(params.get('slot') ?? '');
  const [slotPickerOpen, setSlotPickerOpen] = useState(false);
  const [patient, setPatient] = useState<PatientFormFields>(blankPatientFields);
  const debouncedPhoneSearch = usePatientLookupSearch(patient.phone ?? '');
  const debouncedNameSearch = usePatientLookupSearch(patient.name);
  const phonePatients = useOpdLookup('appointment-patients-phone', debouncedPhoneSearch);
  const namePatients = useOpdLookup('appointment-patients-name', debouncedNameSearch);
  const patientReady = Boolean(patient.name.trim() && patient.phone?.trim() && patient.gender && patient.address?.trim());
  const formReady = Boolean(department && doctor && slot && patientReady);
  const [showErrors, setShowErrors] = useState(false);
  const validateAppointmentFields = () => {
    const nextErrors: Record<string, string> = {};
    if (!department) nextErrors.department = 'Department is required.';
    if (!doctor) nextErrors.doctor = 'Doctor is required.';
    else if (!slot) nextErrors.slot = 'Available Slot is required.';
    if (!patient.name.trim()) nextErrors.patientName = "Patient's name is required.";
    if (!(patient.phone ?? '').trim()) nextErrors.phone = 'Phone is required.';
    else if (!/^\d{10}$/.test((patient.phone ?? '').trim())) nextErrors.phone = 'Enter a 10 digit phone number.';
    if (!patient.gender) nextErrors.gender = 'Gender is required.';
    if (!(patient.address ?? '').trim()) nextErrors.address = 'Address is required.';
    return nextErrors;
  };
  const errors = showErrors ? validateAppointmentFields() : {};

  useEffect(() => {
    if (!doctor || department) return;
    const selected = doctors.data?.find((item) => item.id === doctor);
    if (selected?.departmentId) setDepartment(selected.departmentId);
  }, [department, doctor, doctors.data]);

  const changePatientSearch = (field: 'name' | 'phone', value: string) => {
    setPatient((current) => {
      const next = current.patientId ? blankPatientFields() : current;
      return { ...next, [field]: field === 'phone' ? value || null : value };
    });
  };

  const choosePatient = (options: OpdLookupOption[] | undefined, id: string) => {
    const option = options?.find((item) => item.id === id);
    setPatient(option ? patientFieldsFromOption(option) : blankPatientFields());
  };

  const changeDoctor = (value: string) => {
    setDoctor(value);
    setSlot('');
    setSlotPickerOpen(Boolean(value));
  };

  const chooseSlot = (value: string) => {
    setSlot(value);
    setSlotPickerOpen(false);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setShowErrors(true);
    if (!formReady) return;
    try {
      await mutation.mutateAsync({ ...patient, department, doctor, slot });
      showToast('Appointment saved successfully.', 'success');
      void navigate('/opd/appointments');
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'Appointment could not be saved.', 'error');
    }
  };

  return (
    <div className="page-stack opd-page">
      <div className="page-heading">
        <div><p className="eyebrow">OPD</p><h1>New Enquiry</h1><p>Book a patient into an available doctor schedule.</p></div>
        <Link className="button button-secondary" to="/opd/appointments">Back to Appointments</Link>
      </div>
      <form className="content-card opd-registration-card opd-enquiry-card" noValidate onSubmit={(event) => void submit(event)}>
        <section className="opd-section">
          <h2>Appointment Details</h2>
          <div className="opd-enquiry-row opd-enquiry-appointment-row">
            <label><FieldLabel>Department</FieldLabel><LookupSelect id="appointment-department" value={department} options={departments.data} placeholder="All" emptyOptionLabel="All" ariaInvalid={Boolean(errors.department)} onChange={(value) => { setDepartment(value); changeDoctor(''); setSlotPickerOpen(false); }} /><FieldError message={errors.department} /></label>
            <label><FieldLabel required>Doctor</FieldLabel><LookupSelect id="appointment-doctor" value={doctor} options={doctors.data} placeholder="Select Doctor" clearable={false} ariaInvalid={Boolean(errors.doctor)} onChange={changeDoctor} /><FieldError message={errors.doctor} /></label>
            <label><FieldLabel required>Available Slot</FieldLabel><LookupSelect id="appointment-slot" value={slot} options={schedules.data?.map((item) => ({ id: item.id, label: `${formatOpdScheduleDate(item.date)}, ${formatOpdScheduleTime(item.date, item.fromTime)} - ${formatOpdScheduleTime(item.date, item.toTime)} | ${item.booked}/${item.patientPerSlot}` })).filter((item) => schedules.data?.find((slotItem) => slotItem.id === item.id)?.available)} placeholder="Select date and slot" clearable={false} disabled={!doctor} ariaInvalid={Boolean(errors.slot)} onChange={setSlot} /><FieldError message={errors.slot} /></label>
            <label><FieldLabel>UHID</FieldLabel><input value={patient.uhid ?? ''} placeholder="Filled after patient selection" readOnly /></label>
          </div>
          <div className="opd-enquiry-row opd-enquiry-patient-row">
            <label>
              <FieldLabel required>Patient&apos;s name</FieldLabel>
              <PatientUhidAutocomplete
                id="appointment-patient-name"
                mode="name"
                value={patient.name}
                selectedId={patient.patientId}
                options={namePatients.data}
                isFetching={namePatients.isFetching}
                isError={namePatients.isError}
                placeholder="Type patient name"
                ariaInvalid={Boolean(errors.patientName)}
                onInputChange={(value) => changePatientSearch('name', value)}
                onChange={(id) => choosePatient(namePatients.data, id)}
              />
              <FieldError message={errors.patientName} />
            </label>
            <label>
              <FieldLabel required>Phone</FieldLabel>
              <PatientUhidAutocomplete
                id="appointment-patient-phone"
                mode="phone"
                value={patient.phone ?? ''}
                selectedId={patient.patientId}
                options={phonePatients.data}
                isFetching={phonePatients.isFetching}
                isError={phonePatients.isError}
                placeholder="Type patient phone number"
                ariaInvalid={Boolean(errors.phone)}
                onInputChange={(value) => changePatientSearch('phone', value)}
                onChange={(id) => choosePatient(phonePatients.data, id)}
              />
              <FieldError message={errors.phone} />
            </label>
            <label><FieldLabel required>Gender</FieldLabel><select value={patient.gender} onChange={(event) => setPatient((current) => ({ ...current, gender: event.target.value as PatientFormFields['gender'] }))} required aria-invalid={Boolean(errors.gender)}><option value="" disabled>Select Gender</option><option>Male</option><option>Female</option><option>Others</option></select><FieldError message={errors.gender} /></label>
          </div>
          <div className="opd-enquiry-row opd-enquiry-demographic-row">
            <PatientDemographicFields value={patient} onChange={setPatient} errors={{ address: errors.address, state: errors.state }} />
          </div>
        </section>
        <div className="opd-form-actions"><button className="button button-primary" type="submit" disabled={mutation.isPending}>Save &amp; New</button></div>
      </form>
      <Modal open={slotPickerOpen && doctor !== null} labelledBy="appointment-slot-picker-title" className="opd-slot-modal">
        {doctor ? (
          <>
            <header>
              <div><p>Doctor Schedule</p><h2 id="appointment-slot-picker-title">Select Booking Slot</h2></div>
              <button type="button" onClick={() => setSlotPickerOpen(false)}>×</button>
            </header>
            <div className="opd-slot-modal-body">
              {schedules.isLoading ? <LoadingState /> : null}
              {schedules.isError ? <div className="inline-alert">{schedules.error instanceof ApiError ? schedules.error.message : 'Doctor schedule could not be loaded.'}</div> : null}
              {schedules.isSuccess && schedules.data.length === 0 ? <EmptyState title="No schedule found" message="Create active slots in Time Schedule Setup." /> : null}
              <div className="opd-doctor-schedule-grid">
                {schedules.data?.map((schedule) => (
                  <button
                    type="button"
                    key={schedule.id}
                    className={`${schedule.available ? 'available' : 'full'}${schedule.id === slot ? ' selected' : ''}`}
                    disabled={!schedule.available}
                    onClick={() => chooseSlot(schedule.id)}
                  >
                    <strong>{formatOpdScheduleDate(schedule.date)}</strong>
                    <span>{formatOpdScheduleTime(schedule.date, schedule.fromTime)} - {formatOpdScheduleTime(schedule.date, schedule.toTime)}</span>
                    <small>{schedule.booked} / {schedule.patientPerSlot}</small>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
};
